import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProgressDots } from './components/ProgressDots';
import { SkipButton } from './components/SkipButton';
import { NavArrows } from './components/NavArrows';
import { Slide1Welcome } from './slides/Slide1Welcome';
import { Slide2Trade } from './slides/Slide2Trade';
import { Slide3Source } from './slides/Slide3Source';
import { Slide4Journey } from './slides/Slide4Journey';
import { Slide5CTA } from './slides/Slide5CTA';
import { useSlideTimer } from './hooks/useSlideTimer';
import { useSwipeGesture } from './hooks/useSwipeGesture';
import { useKeyboardNav } from './hooks/useKeyboardNav';
import './BoardingScreen.css';

const TOTAL = 5;
const API_BASE = process.env['REACT_APP_API_URL'] || 'http://localhost:8000/api/portal';

interface BackgroundImages {
  slide2?: string;
  slide3?: string;
  slide4?: string;
}

interface Props {
  investorName: string;
}

const BoardingScreen: React.FC<Props> = ({ investorName }) => {
  const navigate = useNavigate();
  const shellRef = useRef<HTMLDivElement>(null);

  const [mounted, setMounted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [images, setImages] = useState<BackgroundImages>({});

  // Trigger entrance fade on first render
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Fetch background images from public API on mount
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const [productsRes, journeysRes] = await Promise.allSettled([
          fetch(`${API_BASE}/public/products`),
          fetch(`${API_BASE}/public/journeys`),
        ]);

        const result: BackgroundImages = {};

        if (productsRes.status === 'fulfilled' && productsRes.value.ok) {
          const data = await productsRes.value.json() as { products?: Array<{ coverImageUrl?: string; section?: string }> };
          const products = data.products ?? [];
          // Slide 2: prefer artisanal product (more "trade" visual)
          const artisanal = products.find((p) => p.section === 'artisanal' && p.coverImageUrl);
          const any2 = products.find((p) => p.coverImageUrl);
          result.slide2 = artisanal?.coverImageUrl ?? any2?.coverImageUrl;
          // Slide 3: prefer food/source product, different from slide 2
          const food = products.find((p) => p.section === 'food' && p.coverImageUrl);
          const any3 = products.find((p) => p.coverImageUrl && p.coverImageUrl !== result.slide2);
          result.slide3 = food?.coverImageUrl ?? any3?.coverImageUrl ?? any2?.coverImageUrl;
        }

        if (journeysRes.status === 'fulfilled' && journeysRes.value.ok) {
          const data = await journeysRes.value.json() as { journeys?: Array<{ coverImageUrl?: string }> };
          const journeys = data.journeys ?? [];
          result.slide4 = journeys.find((j) => j.coverImageUrl)?.coverImageUrl;
        }

        setImages(result);
      } catch {
        // fallback gradients used automatically if images is empty
      }
    };

    void fetchImages();
  }, []);

  const goToSlide = useCallback((next: number) => {
    if (transitioning || next === current || next < 0 || next >= TOTAL) return;
    setPaused(true);
    setPrev(current);
    setCurrent(next);
    setTransitioning(true);
    setTimeout(() => {
      setPrev(null);
      setTransitioning(false);
      setPaused(false);
    }, 650);
  }, [current, transitioning]);

  const next = useCallback(() => goToSlide(current + 1), [current, goToSlide]);
  const prev2 = useCallback(() => goToSlide(current - 1), [current, goToSlide]);

  const handleSkip = useCallback(() => {
    localStorage.setItem('nomadme_boarding_completed', 'true');
    navigate('/dashboard');
  }, [navigate]);

  const handleStart = useCallback(() => {
    localStorage.setItem('nomadme_boarding_completed', 'true');
    navigate('/dashboard');
  }, [navigate]);

  useSlideTimer(current, next, paused);
  useSwipeGesture(shellRef as React.RefObject<HTMLElement>, next, prev2);
  useKeyboardNav(next, prev2, handleSkip);

  const slideClass = (index: number) => {
    if (index === current) return `bs-slide${mounted ? ' bs-slide--active' : ''}`;
    if (index === prev) return 'bs-slide bs-slide--out';
    return 'bs-slide bs-slide--hidden';
  };

  return (
    <div ref={shellRef} className="bs-shell" role="main" aria-label="NomadMe introduction">
      {/* Slide stack */}
      <div className={slideClass(0)} aria-hidden={current !== 0}>
        <Slide1Welcome investorName={investorName} />
      </div>
      <div className={slideClass(1)} aria-hidden={current !== 1}>
        <Slide2Trade bgImage={images.slide2} />
      </div>
      <div className={slideClass(2)} aria-hidden={current !== 2}>
        <Slide3Source bgImage={images.slide3} />
      </div>
      <div className={slideClass(3)} aria-hidden={current !== 3}>
        <Slide4Journey bgImage={images.slide4} />
      </div>
      <div className={slideClass(4)} aria-hidden={current !== 4}>
        <Slide5CTA onStart={handleStart} />
      </div>

      {/* Controls — always on top */}
      <div className="bs-controls">
        <SkipButton onSkip={handleSkip} />
        <ProgressDots total={TOTAL} current={current} onDotClick={goToSlide} />
        <NavArrows
          onPrev={prev2}
          onNext={next}
          canPrev={current > 0}
          canNext={current < TOTAL - 1}
        />
      </div>
    </div>
  );
};

export default BoardingScreen;
